// ─────────────────────────────────────────────────────────────
//  Results Page — Complete Rewrite with fixed rendering + premium UI
// ─────────────────────────────────────────────────────────────
import type { RankedEntity, SearchResult } from "@top5/shared";
import { search, vote, subscribeSSE, trackSponsorClick } from "../api/client";
import { createSearchBar } from "../components/SearchBar";
import { createRankCard, animateSwap } from "../components/RankCard";
import { createUserHeaderWidget } from "../components/UserHeaderWidget";
import { showAddEntityModal } from "../components/AddEntityModal";

const INTENT_LABELS: Record<string, string> = {
  geo:        "📍 สถานที่",
  web3:       "⛓️ Web3",
  dev:        "💻 Developer",
  popculture: "🎬 Pop Culture",
  academic:   "🔭 Academic",
  general:    "🔍 ทั่วไป",
};

export async function renderResultsPage(
  container: HTMLElement,
  query: string,
  coords: { lat: number; lng: number } | undefined,
  onBackToHome: () => void,
  onNewSearch: (q: string, c?: { lat: number; lng: number }) => void
): Promise<void> {
  container.innerHTML = "";

  // ── Page shell ─────────────────────────────────────────────
  const page = document.createElement("div");
  page.className = "results-page";

  // Header
  const header = document.createElement("header");
  header.className = "results-header";

  const headerInner = document.createElement("div");
  headerInner.className = "results-header-inner";

  const logo = document.createElement("div");
  logo.className = "results-logo";
  logo.textContent = "Top5";
  logo.addEventListener("click", onBackToHome);

  const compactBar = createSearchBar({
    compact: true,
    initialValue: query,
    onSearch: (q) => onNewSearch(q),
  });

  headerInner.appendChild(logo);
  headerInner.appendChild(compactBar);

  const userWidget = await createUserHeaderWidget();
  headerInner.appendChild(userWidget);

  header.appendChild(headerInner);

  // Body
  const body = document.createElement("main");
  body.className = "results-body";

  page.appendChild(header);
  page.appendChild(body);
  container.appendChild(page);

  // ── Skeleton loading ───────────────────────────────────────
  showSkeleton(body);

  // ── Fetch with timeout ─────────────────────────────────────
  let result: SearchResult | null = null;
  try {
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), 40000)
    );
    result = await Promise.race([search(query, coords), timeout]);
  } catch (err) {
    const msg = (err as Error).message === "timeout"
      ? "AI กำลังคิดนานเกินไป ลองกดค้นหาซ้ำอีกครั้ง (ข้อมูลอาจถูกสร้างเสร็จแล้ว)"
      : "เชื่อมต่อ Worker ไม่ได้ — ตรวจสอบว่า `wrangler dev` กำลังรันอยู่";
    showError(body, msg);
    return;
  }

  if (!result) { showError(body, "ไม่ได้รับข้อมูลจาก API"); return; }

  // ── Render results ─────────────────────────────────────────
  renderResults(body, result, query, onNewSearch);

  // ── SSE live updates ───────────────────────────────────────
  let sseSource: EventSource | null = null;
  try {
    sseSource = subscribeSSE(query, (data) => {
      const evt = data as { type: string; data?: { top5: RankedEntity[]; promoted: RankedEntity } };
      if (evt.type === "rank_swap" && evt.data && result) {
        renderResults(body, { ...result, top5: evt.data.top5 }, query, onNewSearch);
        setTimeout(() => {
          const card = body.querySelector<HTMLElement>(`[data-entity-id="${evt.data!.promoted.entity_id}"]`);
          if (card) animateSwap(card);
        }, 50);
      }
    });
  } catch { /* SSE optional */ }

  window.addEventListener("popstate", () => sseSource?.close(), { once: true });
}

// ─────────────────────────────────────────────────────────────
function renderResults(
  body: HTMLElement,
  result: SearchResult,
  query: string,
  onNewSearch: (q: string) => void
): void {
  body.innerHTML = "";

  // Meta row
  const meta = document.createElement("div");
  meta.className = "results-meta";

  const intentKey = result.intent in INTENT_LABELS ? result.intent : "general";

  let metaHtml = `
    <span class="results-query-text">
      ผลลัพธ์สำหรับ <strong>"${escapeHtml(query)}"</strong>
      &nbsp;—&nbsp; ${result.top5.length} อันดับ
    </span>
    <span class="intent-tag ${intentKey}">${INTENT_LABELS[intentKey]}</span>
    ${result.cached ? `<span class="cached-badge">⚡ Cached</span>` : ""}
    <span class="latency-badge">${result.latency_ms}ms</span>
    <span class="live-indicator"><span class="live-dot"></span>Live</span>
  `;

  if (result.did_you_mean) {
    metaHtml += `
      <div class="did-you-mean">
        คุณหมายถึง <button class="did-you-mean-btn" data-q="${escapeHtml(result.did_you_mean)}">"${escapeHtml(result.did_you_mean)}"</button> หรือเปล่า?
      </div>
    `;
  }

  meta.innerHTML = metaHtml;

  if (result.did_you_mean) {
    const btn = meta.querySelector<HTMLButtonElement>(".did-you-mean-btn");
    btn?.addEventListener("click", () => onNewSearch(btn.dataset.q!));
  }

  body.appendChild(meta);

  // Sponsored Banner / Card Section
  if (result.sponsors && result.sponsors.length > 0) {
    const sponsorSection = document.createElement("div");
    sponsorSection.className = "sponsor-section";

    result.sponsors.forEach((sp) => {
      const card = document.createElement("a");
      card.className = "sponsor-card";
      card.href = sp.target_url;
      card.target = "_blank";
      card.rel = "noopener noreferrer";
      card.innerHTML = `
        <div class="sponsor-badge-label">${escapeHtml(sp.badge_text || "⭐ สปอนเซอร์")}</div>
        ${sp.image_url ? `<div class="sponsor-img-wrap"><img src="${escapeHtml(sp.image_url)}" class="sponsor-img" alt="${escapeHtml(sp.title)}" /></div>` : ""}
        <div class="sponsor-content">
          <h4 class="sponsor-title">${escapeHtml(sp.title)}</h4>
          ${sp.description ? `<p class="sponsor-desc">${escapeHtml(sp.description)}</p>` : ""}
          <span class="sponsor-brand">ผู้สนับสนุน: ${escapeHtml(sp.sponsor_name)} ↗</span>
        </div>
      `;

      card.addEventListener("click", () => {
        trackSponsorClick(sp.sponsor_id);
      });

      sponsorSection.appendChild(card);
    });

    body.appendChild(sponsorSection);
  }

  // Rank list
  const rankList = document.createElement("div");
  rankList.className = "rank-list";

  if (!result.top5 || result.top5.length === 0) {
    rankList.innerHTML = `
      <div class="empty-state">
        <div class="empty-icon">🔍</div>
        <h2 class="empty-title">ไม่พบผลลัพธ์สำหรับ "${escapeHtml(query)}"</h2>
        <p class="empty-desc">ลองค้นหาด้วยคำอื่น เช่น กะเพรา, bitcoin, python, คนหล่อ</p>
        <div class="empty-suggestions">
          ${["กะเพรา", "Bitcoin", "Python", "Lee Min-ho", "หลุมดำ"]
            .map((s) => `<button class="suggestion-chip" data-q="${s}">${s}</button>`)
            .join("")}
        </div>
      </div>
    `;
    // Wire suggestion chips
    rankList.querySelectorAll<HTMLButtonElement>(".suggestion-chip").forEach((btn) => {
      btn.addEventListener("click", () => onNewSearch(btn.dataset.q!));
    });
  } else {
    const handleVote = async (entityId: string) => {
      try {
        const vr = await vote(entityId, query);
        if (vr.rank_changed && "top5" in vr && Array.isArray(vr.top5)) {
          renderResults(
            body,
            {
              ...result,
              top5: vr.top5 as RankedEntity[],
              challenger_pool: (vr.challenger_pool as RankedEntity[]) ?? result.challenger_pool
            },
            query,
            onNewSearch
          );
          setTimeout(() => {
            const c = body.querySelector<HTMLElement>(`[data-entity-id="${entityId}"]`);
            if (c) animateSwap(c);
          }, 50);
        }
        return { success: vr.success ?? true, new_upvotes: vr.new_upvotes ?? 0 };
      } catch {
        return null;
      }
    };

    result.top5.forEach((entity, i) => {
      // Ensure intent is set
      const enriched: RankedEntity = {
        ...entity,
        intent: entity.intent ?? (result.intent as RankedEntity["intent"]),
        rank: entity.rank ?? i + 1,
      };
      const card = createRankCard(enriched, query, handleVote);
      rankList.appendChild(card);
    });

    body.appendChild(rankList);

    // Challenger Pool
    if (result.challenger_pool && result.challenger_pool.length > 0) {
      body.appendChild(buildChallengerSection(result.challenger_pool, handleVote));
    }

    // Add Custom Entity Action Bar
    const addEntityBar = document.createElement("div");
    addEntityBar.className = "add-entity-bar";
    addEntityBar.innerHTML = `
      <button class="add-entity-btn" id="propose-new-entity-btn">
        <span>➕</span> เสนอตัวเลือกใหม่สำหรับ "${escapeHtml(query)}"
      </button>
    `;

    const proposeBtn = addEntityBar.querySelector<HTMLButtonElement>("#propose-new-entity-btn")!;
    proposeBtn.addEventListener("click", () => {
      showAddEntityModal(query, (data) => {
        if (data.top5 && Array.isArray(data.top5)) {
          renderResults(
            body,
            {
              ...result,
              top5: data.top5 as RankedEntity[],
              challenger_pool: (data.challenger_pool as RankedEntity[]) ?? result.challenger_pool
            },
            query,
            onNewSearch
          );
        }
      });
    });

    body.appendChild(addEntityBar);
  }
}

// ─────────────────────────────────────────────────────────────
function buildChallengerSection(
  pool: RankedEntity[],
  onVote: (entityId: string) => Promise<{ success: boolean; new_upvotes: number } | null>
): HTMLElement {
  const sec = document.createElement("section");
  sec.className = "challenger-section";

  const toggle = document.createElement("button");
  toggle.className = "challenger-toggle";
  toggle.innerHTML = `
    <span class="challenger-icon">⚔️</span>
    <span>ผู้ท้าชิง — อันดับ 6–${Math.min(5 + pool.length, 20)}</span>
    <span class="challenger-sub">โหวตดันขึ้น Top5 ได้!</span>
    <span class="chevron">▼</span>
  `;

  const list = document.createElement("div");
  list.className = "challenger-list";
  pool.forEach((e) => {
    const row = document.createElement("div");
    row.className = "challenger-mini";
    row.innerHTML = `
      <span class="challenger-rank">#${e.rank}</span>
      <span class="challenger-name">
        ${escapeHtml(e.entity_name)}
        ${e.created_by_username ? `<span class="challenger-creator-badge">👤 @${escapeHtml(e.created_by_username)}</span>` : ""}
      </span>
      <span class="challenger-score">${e.total_score.toFixed(1)} pts</span>
      <button class="challenger-vote-btn" data-entity-id="${escapeHtml(e.entity_id)}" title="โหวตดันขึ้น Top5">
        <span class="challenger-vote-icon">▲</span>
        <span class="challenger-vote-count">${e.upvotes ?? 0}</span>
      </button>
    `;

    const voteBtn = row.querySelector<HTMLButtonElement>(".challenger-vote-btn");
    const countEl = row.querySelector<HTMLElement>(".challenger-vote-count");
    if (voteBtn && countEl) {
      voteBtn.addEventListener("click", async (evt) => {
        evt.stopPropagation();
        if (voteBtn.classList.contains("voted") || voteBtn.classList.contains("voting")) return;
        voteBtn.classList.add("voting");
        const currentCount = parseInt(countEl.textContent || "0", 10);
        countEl.textContent = String(currentCount + 1);
        try {
          const vr = await onVote(e.entity_id);
          if (vr && typeof vr.new_upvotes === "number") {
            countEl.textContent = String(vr.new_upvotes);
          }
          if (vr && !vr.success) {
            countEl.textContent = String(vr.new_upvotes ?? e.upvotes ?? 0);
            voteBtn.classList.add("voted");
            voteBtn.title = "โหวตแล้วใน 24 ชั่วโมงนี้";
          } else {
            voteBtn.classList.add("voted");
          }
        } catch {
          countEl.textContent = String(e.upvotes ?? 0);
        } finally {
          voteBtn.classList.remove("voting");
        }
      });
    }

    list.appendChild(row);
  });

  toggle.addEventListener("click", () => {
    toggle.classList.toggle("open");
    list.classList.toggle("open");
  });

  sec.appendChild(toggle);
  sec.appendChild(list);
  return sec;
}

// ─────────────────────────────────────────────────────────────
function showSkeleton(body: HTMLElement): void {
  body.innerHTML = `
    <div class="skeleton-header-ai">
      <div class="ai-spinner">🤖</div>
      <span>กำลังค้นหาข้อมูลและใช้ AI วิเคราะห์จากทั่วโลก...</span>
    </div>
    <div class="results-meta">
      <div class="skeleton" style="width:220px;height:22px;border-radius:99px;"></div>
      <div class="skeleton" style="width:80px;height:22px;border-radius:99px;"></div>
    </div>
    <div class="rank-list">
      ${[1,2,3,4,5].map((i) => `
        <div class="rank-card skeleton-card" style="animation-delay:${(i-1)*60}ms">
          <div class="rank-medal"><div class="skeleton" style="width:28px;height:28px;border-radius:50%;"></div></div>
          <div class="rank-card-img"><div class="skeleton" style="width:100%;height:100%;"></div></div>
          <div class="rank-card-body" style="gap:10px;">
            <div class="skeleton" style="width:60%;height:20px;border-radius:6px;"></div>
            <div class="skeleton" style="width:100%;height:14px;border-radius:6px;"></div>
            <div class="skeleton" style="width:80%;height:14px;border-radius:6px;"></div>
            <div class="skeleton" style="width:40%;height:18px;border-radius:99px;"></div>
          </div>
          <div class="rank-card-actions">
            <div class="skeleton" style="width:56px;height:56px;border-radius:10px;"></div>
          </div>
        </div>
      `).join("")}
    </div>
  `;
}

function showError(body: HTMLElement, msg: string): void {
  body.innerHTML = `
    <div class="error-state">
      <div class="error-icon">😵</div>
      <div class="error-title">เกิดข้อผิดพลาด</div>
      <div class="error-desc">${escapeHtml(msg)}</div>
    </div>
  `;
}

function escapeHtml(str: string): string {
  return str.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
}
